import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Save, Plus, Trash, X, Printer } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PhotoEvidenceModule } from '../attachments/PhotoEvidenceModule';
import { printMovement } from '../print/printMovement';
import { api } from '../../services/api';
import { Modal } from '../Modal/Modal';
import { useUserPrefs } from '../context/UserPrefs';
import recordOperationsDownload from '../../utils/recordOperationsDownload';
import { PENDING_OC_STATUSES } from './purchaseOrderStatus';

export function EntryModule({ materials, brandItems, warehouses = [
  { id: 'popayan', name: 'Popayán' },
  { id: 'santander', name: 'Santander de Quilichao' },
], location: siteLocation = '', onRegister, onRegisterBulk, onNewAssignment, onAdd }) {
  const { currentUser } = useUserPrefs();
  const [type, setType] = useState('Compras');
  const [materialId, setMaterialId] = useState('');
  const [materialQuery, setMaterialQuery] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [supplier, setSupplier] = useState('');
  const [location, setLocation] = useState('');
  const [observations, setObservations] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [serial, setSerial] = useState('');
  const [showSerialField, setShowSerialField] = useState(false);
  const [items, setItems] = useState([]); // lista de ítems en esta entrada
  const [serialOption, setSerialOption] = useState('No'); // 'Sí' | 'No'
  const [selectedBrand, setSelectedBrand] = useState('');
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || 'popayan');
  const [categoryFilter, setCategoryFilter] = useState('Todas');
  const [evidencePhotos, setEvidencePhotos] = useState([]);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [extraMaterialsByCat, setExtraMaterialsByCat] = useState([]);
  const [isMaterialDropdownOpen, setIsMaterialDropdownOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParsed, setBulkParsed] = useState(null);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const wrapperRef = useRef(null);

  // Vínculo con orden de compra (OC): permite prellenar los ítems de la
  // entrada desde una OC pendiente y, al guardar, recepcionarla.
  const [ocOptions, setOcOptions] = useState([]);
  const [ocLoading, setOcLoading] = useState(false);
  const [selectedOcId, setSelectedOcId] = useState('');
  const [ocReviewRows, setOcReviewRows] = useState([]);
  const [linkedOcs, setLinkedOcs] = useState({}); // { [ocId]: ocObjetoCompleto }

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsMaterialDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Cargar órdenes de compra pendientes por llegar (para vincular la entrada)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOcLoading(true);
      try {
        const res = await api.accounting.purchaseOrders.list({ location: siteLocation, limit: 500, order: 'desc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const pending = rows.filter((o) => PENDING_OC_STATUSES.includes(String(o?.status || '')));
        if (!cancelled) setOcOptions(pending);
      } catch (_) {
        if (!cancelled) setOcOptions([]);
      } finally {
        if (!cancelled) setOcLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteLocation]);

  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  // Eliminado flujo de guardar/cancelar del formulario de material inline

  // Material seleccionado para mostrar sus campos tal como en Existencias
  const selectedMaterial = useMemo(() => {
    const id = parseInt(materialId);
    return materials.find((m) => m.id === id) || null;
  }, [materialId, materials]);

  const requiresSerialMaterial = useMemo(() => {
    if (!selectedMaterial) return false;
    const reqDb = (selectedMaterial?.requires_serial === 1) || (selectedMaterial?.requires_serial === true) || (String(selectedMaterial?.requires_serial || '').toLowerCase() === 'true');
    const hasSerialFlag = String(selectedMaterial?.hasSerial || '').toLowerCase() === 'sí';
    const reqFlag = Boolean(selectedMaterial?.requiresSerial);
    const v = String(selectedMaterial?.serial || '').trim();
    const meaningful = v && !/^NS-\d+-\d+$/.test(v);
    return reqDb || reqFlag || hasSerialFlag || meaningful;
  }, [selectedMaterial]);

  useEffect(() => {
    setMaterialId('');
    setMaterialQuery('');
  }, []);

  useEffect(() => {
    if (requiresSerialMaterial) {
      setSerialOption('Sí');
      setShowSerialField(true);
      return;
    }
    setSerialOption('No');
    setShowSerialField(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterial]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (materials || []).forEach((m) => {
      const c = String(m.category || m.type || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials]);

  const materialOptions = useMemo(() => {
    const base = Array.isArray(materials) ? materials : [];
    const extra = Array.isArray(extraMaterialsByCat) ? extraMaterialsByCat : [];
    const union = [...base, ...extra];
    // Deduplicar por id o código
    const seen = new Set();
    const list = union.filter((m) => {
      const key = String(m.id || m.code || '').trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const filtered = (categoryFilter && categoryFilter !== 'Todas')
      ? list.filter((m) => {
          const cat = String(m.category || m.type || '').trim();
          return norm(cat).includes(norm(categoryFilter));
        })
      : list;
    return filtered.map((m) => ({
      id: m.id,
      label: m.code ? `${m.code} - ${m.name}` : `${m.id} - ${m.name}`
    }));
  }, [materials, extraMaterialsByCat, categoryFilter]);

  const filteredMaterialOptions = useMemo(() => {
    if (!materialQuery) return materialOptions;
    const q = norm(materialQuery);
    return materialOptions.filter(o => norm(o.label).includes(q));
  }, [materialOptions, materialQuery]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cat = String(categoryFilter || '').trim();
      if (!cat || cat === 'Todas') { setExtraMaterialsByCat([]); return; }
      try {
        // Traer todo y filtrar por coincidencia flexible
        const res = await api.materials.get({ limit: 5000 });
        const rows = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        const normalized = rows.map(m => ({
          id: m.id,
          code: m.code,
          name: m.name,
          brand: m.brand || '',
          category: m.category || '',
          type: m.category || '',
          unit: m.unit || 'Unidad',
          status: m.status || 'Activo',
        }));
        const filtered = normalized.filter((m) => norm(String(m.category || m.type || '')).includes(norm(cat)));
        if (!cancelled) setExtraMaterialsByCat(filtered);
      } catch (_) {
        if (!cancelled) setExtraMaterialsByCat([]);
      }
    })();
    return () => { cancelled = true; };
  }, [categoryFilter]);

  useEffect(() => {
    setMaterialId('');
    setMaterialQuery('');
  }, [categoryFilter]);


  // Marcas disponibles por tipo (no por material específico)
  const brandsForMaterial = useMemo(() => {
    const selectedType = selectedMaterial?.type || selectedMaterial?.category || '';
    if (!selectedType) return [];
    const byType = (brandItems || []).filter((b) => {
      const biType = (b.materialType || '').trim();
      if (biType) return (biType === selectedType) && (b.status === 'Activo');
      const code = (b.materialCode || '').trim();
      if (!code) return false;
      const mat = materials.find(m => String(m.code) === String(code));
      const mt = mat?.type || mat?.category || '';
      return (mt === selectedType) && (b.status === 'Activo');
    });
    // Deduplicar por nombre de marca
    const seen = new Set();
    const unique = [];
    for (const b of byType) {
      const nm = String(b.name || '').trim().toLowerCase();
      if (!nm) continue;
      if (!seen.has(nm)) { seen.add(nm); unique.push(b); }
    }
    return unique;
  }, [brandItems, selectedMaterial, materials]);

  useEffect(() => {
    if (!selectedBrand) return;
    const stillValid = (brandsForMaterial || []).some(b => b.name === selectedBrand);
    if (!stillValid) setSelectedBrand('');
  }, [selectedMaterial, brandsForMaterial, selectedBrand]);

  const canSubmitSingle = useMemo(() => {
    return materialId && Number(quantity) > 0 && type;
  }, [materialId, quantity, type]);

  const canSubmitBulk = useMemo(() => {
    return items.length > 0 || canSubmitSingle;
  }, [items.length, canSubmitSingle]);

  // Prefijar la ubicación con la del material seleccionado
  useEffect(() => {
    setLocation(selectedMaterial?.location || '');
  }, [selectedMaterial]);

  // Sincronizar texto del buscador con el material seleccionado (REMOVED)
  // useEffect(() => {
  //   const opt = materialOptions.find((o) => o.id === parseInt(materialId));
  //   setMaterialQuery(opt ? opt.label : '');
  // }, [materialId, materialOptions]);

  // Eliminado handleCancel: se usa lógica inline en el botón Cancelar

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitBulk) return;

    // Generar una referencia única para agrupar esta entrada
    const entryRef = 'ENT-' + new Date().toISOString().replace(/[-:.TZ]/g,'').slice(0,14) + '-' + Math.floor(Math.random()*1000);

    // Preparar el lote de ítems a registrar
    const batch = items.length > 0
      ? items
      : [{ type, materialId, quantity, supplier, location, observations, serial: showSerialField ? (serial || '') : '', brandName: selectedBrand || '', warehouseId, evidencePhotos }];

    const hasOcItems = batch.some((it) => it.ocId);
    if (hasOcItems && (!Array.isArray(evidencePhotos) || evidencePhotos.length === 0)) {
      setStatusMsg('Debes adjuntar al menos 1 foto de evidencia para una entrada vinculada a una orden de compra.');
      return;
    }

    let ok = 0;
    let failed = 0;
    let lastItem = null;
    for (const it of batch) {
      const res = await onRegister({ ...it, warehouseId, entryRef });
      if (res?.success) ok++; else failed++;
      lastItem = it;
    }
    setStatusMsg(`Guardado: ${ok} ok, ${failed} error(es)`);

    if (failed === 0 && hasOcItems && currentUser?.purchaseCanEditOrders) {
      try {
        const ocGroups = {};
        batch.forEach((it) => {
          if (!it.ocId) return;
          const key = String(it.ocId);
          (ocGroups[key] = ocGroups[key] || []).push(it);
        });
        const photoUrls = Array.isArray(evidencePhotos) ? evidencePhotos.map((p) => p?.dataUrl || p).filter(Boolean) : [];
        for (const ocIdKey of Object.keys(ocGroups)) {
          const oc = linkedOcs[ocIdKey];
          if (!oc) continue;
          const enteredByIndex = {};
          ocGroups[ocIdKey].forEach((it) => {
            enteredByIndex[it.ocItemIndex] = (enteredByIndex[it.ocItemIndex] || 0) + (Number(it.quantity) || 0);
          });
          const receptionItems = (Array.isArray(oc.items) ? oc.items : []).map((ocIt, idx) => {
            const prevReceived = Number(oc?.reception?.items?.[idx]?.received || 0) || 0;
            const enteredToday = enteredByIndex[idx] || 0;
            return { ...ocIt, received: prevReceived + enteredToday };
          });
          await api.accounting.purchaseOrders.receive({
            id: oc.id,
            skip_auto_entrada: true,
            reception: {
              date: new Date().toISOString().slice(0, 10),
              receivedBy: currentUser?.name || '',
              reference: entryRef,
              notes: observations || '',
              warehouse_id: warehouseId,
              skip_auto_entrada: true,
              photos: photoUrls,
              items: receptionItems,
            },
          });
        }
      } catch (err) {
        setStatusMsg((prev) => `${prev} — Aviso: la entrada se guardó, pero no se pudo actualizar el estado de la OC (se puede reconciliar luego desde "Verificar llegada").`);
      }
    }

    if (failed === 0) {
      // Construir comprobante usando el lote real (batch), antes de limpiar estado
      try {
        const material = materials.find(m => String(m.id) === String(lastItem.materialId));
        const itemsForPrint = Array.isArray(batch) ? batch.map((it) => {
          const m = materials.find(mm => String(mm.id) === String(it.materialId));
          return {
            materialName: m?.name || String(it.materialId),
            materialCode: m?.code || '',
            materialUnit: m?.unit || 'Unidad',
            unit: m?.unit || 'Unidad',
            quantity: parseInt(it.quantity),
            serial: String(it.serial || '').trim(),
            brandName: String(it.brandName || '').trim()
          };
        }) : undefined;

        const receipt = {
          type: 'Entrada',
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          materialName: material?.name || String(lastItem.materialId),
          materialCode: material?.code,
          material_category: material?.category || '',
          materialUnit: material?.unit || 'Unidad',
          unit: material?.unit || 'Unidad',
          materialLocation: material?.location || lastItem.location || '',
          materialStatus: material?.status,
          quantity: parseInt(lastItem.quantity),
          detail: lastItem.type,
          origin: lastItem.supplier,
          observations: lastItem.observations || '',
          serial: lastItem.serial || '',
          brandName: lastItem.brandName || '',
          evidencePhotos: Array.isArray(lastItem.evidencePhotos) ? lastItem.evidencePhotos : [],
          warehouseId: lastItem.warehouseId,
          entryRef,
          items: itemsForPrint
        };
        setLastReceipt(receipt);
        printMovement(receipt);
      } catch (err) {
        // en caso de error de armado de comprobante, solo no imprimir
      }

      // reiniciar formulario y lista después de construir el comprobante
      setItems([]);
      setMaterialId('');
      setMaterialQuery('');
      setQuantity(1);
      setSupplier('');
      setObservations('');
      setSerial('');
      setShowSerialField(false);
      setSelectedBrand('');
      setEvidencePhotos([]);
      setLinkedOcs({});
      setSelectedOcId('');
      setOcReviewRows([]);
    }
  };

  const handleSerialOptionChange = (val) => {
    if (requiresSerialMaterial) return;
    setSerialOption(val);
    const enable = val === 'Sí';
    setShowSerialField(enable);
    if (!enable) setSerial('');
  };

  const handleAddItem = () => {
    if (!canSubmitSingle) return;
    if ((requiresSerialMaterial || serialOption === 'Sí') && !serial.trim()) {
      setStatusMsg('Ingrese la serie.');
      return;
    }
    const newItem = {
      type,
      materialId,
      quantity,
      supplier,
      location,
      observations,
      serial: (requiresSerialMaterial || serialOption === 'Sí') ? serial.trim() : '',
      brandName: selectedBrand || '',
      warehouseId,
      evidencePhotos,
    };
    setItems((prev) => [...prev, newItem]);
    setQuantity(1);
    setSerial('');
    setSelectedBrand('');
    setMaterialId('');
    setMaterialQuery('');
    setShowSerialField(false);
    setSerialOption('No');
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // --- Vínculo con orden de compra (OC) ---
  const materialsByCodeNorm = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => {
      const code = String(m?.code || '').trim();
      if (code) map.set(norm(code), m);
    });
    return map;
  }, [materials]);
  const materialsByNameNorm = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => {
      const name = String(m?.name || '').trim();
      if (name) map.set(norm(name), m);
    });
    return map;
  }, [materials]);

  // Igual que findMaterial() del import masivo (línea ~378 más abajo): usa
  // material_id si vino en el ítem de la OC, si no intenta por código/nombre.
  const resolveMaterialForOcItem = (ocItem) => {
    const mid = Number(ocItem?.material_id || 0) || 0;
    if (mid > 0) {
      const byId = (materials || []).find((m) => Number(m.id) === mid);
      if (byId) return byId;
    }
    const code = String(ocItem?.material_code || '').trim();
    if (code) {
      const byCode = materialsByCodeNorm.get(norm(code));
      if (byCode) return byCode;
    }
    const name = String(ocItem?.material_name || ocItem?.description || '').trim();
    if (name) {
      const byName = materialsByNameNorm.get(norm(name));
      if (byName) return byName;
    }
    return null;
  };

  const handleSelectOcForReview = (ocId) => {
    setSelectedOcId(ocId);
    const oc = ocOptions.find((o) => String(o.id) === String(ocId));
    if (!oc) { setOcReviewRows([]); return; }
    const ocItems = Array.isArray(oc.items) ? oc.items : [];
    const rows = ocItems.map((it, idx) => {
      const ordered = Number(it?.quantity || 0) || 0;
      const alreadyReceived = Number(oc?.reception?.items?.[idx]?.received || 0) || 0;
      const remaining = Math.max(0, ordered - alreadyReceived);
      const matched = resolveMaterialForOcItem(it);
      return {
        ocItemIndex: idx,
        description: String(it?.description || it?.material_name || it?.material_code || '').trim() || `Ítem ${idx + 1}`,
        unit: it?.unit || 'Unidad',
        ordered,
        alreadyReceived,
        enteredQty: remaining,
        materialId: matched?.id ? String(matched.id) : '',
      };
    });
    setOcReviewRows(rows);
  };

  const updateOcReviewRow = (idx, patch) => {
    setOcReviewRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addOcRowsToBatch = () => {
    const oc = ocOptions.find((o) => String(o.id) === String(selectedOcId));
    if (!oc) return;
    const validRows = ocReviewRows.filter((r) => r.materialId && Number(r.enteredQty) > 0);
    if (!validRows.length) {
      setStatusMsg('Selecciona el material y la cantidad para al menos un ítem de la OC.');
      return;
    }
    const newItems = validRows.map((r) => ({
      type: 'Compras',
      materialId: r.materialId,
      quantity: Number(r.enteredQty),
      supplier: oc.provider || '',
      location: '',
      observations: `OC ${oc.code}`,
      serial: '',
      brandName: '',
      warehouseId,
      evidencePhotos,
      ocId: oc.id,
      ocCode: oc.code,
      ocItemIndex: r.ocItemIndex,
    }));
    setItems((prev) => [...prev, ...newItems]);
    setLinkedOcs((prev) => ({ ...prev, [oc.id]: oc }));
    setSelectedOcId('');
    setOcReviewRows([]);
    setStatusMsg(`${newItems.length} ítem(s) de la OC ${oc.code} agregados al lote.`);
  };

  const downloadBulkTemplate = () => {
    const rows = [
      { Material: 'TEC-003', Marca: 'HP', Serial: 'ABC123', Cantidad: 1 },
      { Material: 'TEC-003', Marca: 'HP', Serial: 'DEF456', Cantidad: 1 },
      { Material: 'TEC-003', Marca: 'GENERICA', Serial: '', Cantidad: 10 },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Entradas');
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Plantilla entrada masiva' });
    XLSX.writeFile(wb, 'plantilla_entrada_masiva.xlsx');
  };

  const parseBulkFile = async (file) => {
    if (!file) return;
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames?.[0];
    const ws = sheetName ? wb.Sheets[sheetName] : null;
    if (!ws) {
      setBulkParsed(null);
      setBulkErrors(['El archivo no tiene hojas.']);
      return;
    }

    const raw = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
    const normHeader = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const getField = (obj, names) => {
      const keys = Object.keys(obj || {});
      for (const k of keys) {
        const nk = normHeader(k);
        if (names.includes(nk)) return obj[k];
      }
      return '';
    };

    const errs = [];
    const rows = [];
    const serialSeen = new Set(); // materialId|serial

    const normText = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const materialsByCode = new Map();
    const materialsByName = new Map();
    (materials || []).forEach((m) => {
      const code = String(m?.code || '').trim();
      const name = String(m?.name || '').trim();
      if (code) materialsByCode.set(normText(code), m);
      if (name) materialsByName.set(normText(name), m);
    });
    const findMaterial = (cell) => {
      const raw = String(cell || '').trim();
      if (!raw) return null;
      if (/^\d+$/.test(raw)) {
        const id = parseInt(raw, 10);
        return (materials || []).find((m) => Number(m?.id) === Number(id)) || null;
      }
      const k = normText(raw);
      return materialsByCode.get(k) || materialsByName.get(k) || null;
    };
    const parseSerials = (cell) => {
      const raw = String(cell || '').trim();
      if (!raw) return [];
      return raw
        .split(/[\r\n,;|]+/g)
        .map((s) => String(s || '').trim())
        .filter(Boolean);
    };

    raw.forEach((r, idx) => {
      const materialCell = String(getField(r, ['material', 'codigo', 'código', 'code'])).trim();
      const brand = String(getField(r, ['marca', 'brand'])).trim();
      const model = String(getField(r, ['modelo', 'model'])).trim();
      const serialCell = String(getField(r, ['serial', 'serie', 'numero de serie', 'numero serie'])).trim();
      const qtyRaw = String(getField(r, ['cantidad', 'qty', 'cantidad unidad', 'unidades'])).trim();
      const qty = parseInt(qtyRaw || '0', 10);
      const line = idx + 2;

      if (!materialCell && !brand && !serialCell && !qtyRaw) return;

      if (!materialCell) {
        errs.push(`Línea ${line}: falta Material.`);
        return;
      }
      const mat = findMaterial(materialCell);
      if (!mat) {
        errs.push(`Línea ${line}: Material no encontrado (${materialCell}).`);
        return;
      }

      const matRequires = Boolean(mat?.requiresSerial) || String(mat?.hasSerial || '').toLowerCase() === 'sí';
      const serials = parseSerials(serialCell);
      const hasSerials = serials.length > 0;
      const wantsSerials = matRequires || hasSerials;

      if (wantsSerials) {
        if (!hasSerials) {
          errs.push(`Línea ${line}: este material requiere serial o el archivo debe incluir Serial.`);
          return;
        }
        if (qtyRaw && Number.isFinite(qty) && qty > 1) errs.push(`Línea ${line}: para seriales la cantidad debe ser 1.`);
        serials.forEach((s) => {
          const key = `${mat.id}|${String(s).toLowerCase()}`;
          if (serialSeen.has(key)) errs.push(`Línea ${line}: serial repetido para este material (${s}).`);
          serialSeen.add(key);
          rows.push({ material: mat, brand, model, serial: s, quantity: 1 });
        });
      } else {
        if (!Number.isFinite(qty) || qty <= 0) {
          errs.push(`Línea ${line}: cantidad inválida.`);
          return;
        }
        rows.push({ material: mat, brand, model, serial: '', quantity: Number.isFinite(qty) ? qty : 0 });
      }
    });

    const byMaterial = new Map();
    rows.forEach((r) => {
      const mat = r.material;
      if (!mat) return;
      const keyMat = String(mat.id);
      if (!byMaterial.has(keyMat)) byMaterial.set(keyMat, { material: mat, serialGroups: new Map(), qtyGroups: new Map() });
      const bucket = byMaterial.get(keyMat);
      const brandKey = String(r.brand || '').trim().toLowerCase();
      const modelKey = String(r.model || '').trim().toLowerCase();
      const gKey = `${brandKey}||${modelKey}`;
      if (r.serial) {
        const existing = bucket.serialGroups.get(gKey) || { brand: r.brand, model: r.model, serials: [] };
        existing.serials.push(r.serial);
        bucket.serialGroups.set(gKey, existing);
      } else {
        const existing = bucket.qtyGroups.get(gKey) || { brand: r.brand, model: r.model, quantity: 0 };
        existing.quantity += Number(r.quantity) || 0;
        bucket.qtyGroups.set(gKey, existing);
      }
    });

    const materialsParsed = Array.from(byMaterial.values()).map((b) => ({
      materialId: String(b.material.id),
      materialCode: b.material.code || '',
      materialName: b.material.name || '',
      materialUnit: b.material.unit || 'Unidad',
      materialCategory: b.material.category || b.material.type || '',
      serialGroups: Array.from(b.serialGroups.values()).filter((g) => (Array.isArray(g.serials) ? g.serials.length : 0) > 0),
      qtyGroups: Array.from(b.qtyGroups.values()).filter((g) => (parseInt(g.quantity) || 0) > 0),
    })).sort((a, b) => String(a.materialCode || '').localeCompare(String(b.materialCode || '')) || String(a.materialName || '').localeCompare(String(b.materialName || '')));

    setBulkErrors(errs);
    setBulkParsed({ materials: materialsParsed, rowsCount: rows.length });
  };

  const submitBulk = async () => {
    if (!bulkParsed?.materials?.length) return;
    if (bulkErrors.length > 0) return;

    setBulkSaving(true);
    try {
      const entryRef = 'ENT-' + new Date().toISOString().replace(/[-:.TZ]/g,'').slice(0,14) + '-' + Math.floor(Math.random()*1000);
      const defaultWarehouseId = warehouses[0]?.id || 'popayan';
      const buildItemsForPrint = () => {
        const flat = [];
        for (const m of bulkParsed.materials) {
          const mName = m.materialName || String(m.materialId || '');
          const mCode = m.materialCode || '';
          const mUnit = m.materialUnit || 'Unidad';
          for (const g of (m.serialGroups || [])) {
            const brandName = String(g.brand || '').trim();
            const serials = Array.isArray(g.serials) ? g.serials : [];
            for (const s of serials) {
              flat.push({
                materialName: mName,
                materialCode: mCode,
                materialUnit: mUnit,
                unit: mUnit,
                quantity: 1,
                serial: String(s || '').trim(),
                brandName: brandName || ''
              });
            }
          }
          for (const g of (m.qtyGroups || [])) {
            const qty = parseInt(g.quantity) || 0;
            if (qty <= 0) continue;
            flat.push({
              materialName: mName,
              materialCode: mCode,
              materialUnit: mUnit,
              unit: mUnit,
              quantity: qty,
              serial: '',
              brandName: String(g.brand || '').trim()
            });
          }
        }
        return flat;
      };

      const totalQtyForPrint = bulkParsed.materials.reduce((acc, m) => {
        const sCount = (m.serialGroups || []).reduce((a, g) => a + (Array.isArray(g.serials) ? g.serials.length : 0), 0);
        const qCount = (m.qtyGroups || []).reduce((a, g) => a + (parseInt(g.quantity) || 0), 0);
        return acc + sCount + qCount;
      }, 0);

      if (typeof onRegisterBulk === 'function') {
        let ok = 0;
        let failed = 0;
        const allSerialErrors = [];
        for (const m of bulkParsed.materials) {
          const matId = String(m.materialId || '').trim();
          if (!matId) continue;
          if ((m.serialGroups || []).length > 0) {
            const resS = await onRegisterBulk({
              materialId: matId,
              warehouseId,
              entryRef,
              supplier,
              observations,
              location,
              detailType: type,
              requiresSerial: true,
              groups: m.serialGroups
            });
            ok += Number(resS?.ok ?? 0) || 0;
            failed += Number(resS?.failed ?? 0) || 0;
            if (Array.isArray(resS?.serialErrors)) allSerialErrors.push(...resS.serialErrors);
          }
          if ((m.qtyGroups || []).length > 0) {
            const resQ = await onRegisterBulk({
              materialId: matId,
              warehouseId,
              entryRef,
              supplier,
              observations,
              location,
              detailType: type,
              requiresSerial: false,
              groups: m.qtyGroups
            });
            ok += Number(resQ?.ok ?? 0) || 0;
            failed += Number(resQ?.failed ?? 0) || 0;
            if (Array.isArray(resQ?.serialErrors)) allSerialErrors.push(...resQ.serialErrors);
          }
        }
        if (allSerialErrors.length > 0) {
          setStatusMsg(`Entrada masiva: ${ok} ok, ${failed} error(es). Seriales con problema: ${allSerialErrors.join(' | ')}`);
        } else {
          setStatusMsg(`Entrada masiva: ${ok} ok, ${failed} error(es)`);
        }
      } else if (typeof onRegister === 'function') {
        let ok = 0;
        let failed = 0;
        for (const m of bulkParsed.materials) {
          const matId = String(m.materialId || '').trim();
          if (!matId) continue;
          for (const g of (m.serialGroups || [])) {
            const brandName = String(g.brand || '').trim();
            const serials = Array.isArray(g.serials) ? g.serials : [];
            for (const s of serials) {
              const payload = {
                type,
                materialId: matId,
                quantity: 1,
                supplier,
                location,
                observations,
                serial: String(s || '').trim(),
                brandName,
                warehouseId,
                evidencePhotos: [],
                entryRef
              };
              const r = await onRegister(payload);
              if (r?.success) ok += 1; else failed += 1;
            }
          }
          for (const g of (m.qtyGroups || [])) {
            const qty = parseInt(g.quantity) || 0;
            if (qty <= 0) continue;
            const payload = {
              type,
              materialId: matId,
              quantity: qty,
              supplier,
              location,
              observations,
              serial: '',
              brandName: String(g.brand || '').trim(),
              warehouseId,
              evidencePhotos: [],
              entryRef
            };
            const r = await onRegister(payload);
            if (r?.success) ok += 1; else failed += 1;
          }
        }
        setStatusMsg(`Entrada masiva: ${ok} ok, ${failed} error(es)`);
      }

      try {
        const itemsForPrint = buildItemsForPrint();
        const firstMat = bulkParsed.materials[0] || {};
        const receipt = {
          type: 'Entrada',
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          materialName: firstMat.materialName || 'Entrada masiva',
          materialCode: firstMat.materialCode || '',
          material_category: firstMat.materialCategory || '',
          materialUnit: firstMat.materialUnit || 'Unidad',
          unit: firstMat.materialUnit || 'Unidad',
          materialLocation: location || '',
          materialStatus: 'Activo',
          quantity: totalQtyForPrint,
          detail: type,
          origin: supplier,
          observations: observations || '',
          serial: '',
          brandName: '',
          evidencePhotos: [],
          warehouseId,
          entryRef,
          items: itemsForPrint
        };
        setLastReceipt(receipt);
        printMovement(receipt);
      } catch (_) {}

      setBulkOpen(false);
      setBulkParsed(null);
      setBulkFileName('');
      setBulkErrors([]);

      setItems([]);
      setType('Compras');
      setWarehouseId(defaultWarehouseId);
      setCategoryFilter('Todas');
      setMaterialId('');
      setMaterialQuery('');
      setQuantity(1);
      setSupplier('');
      setLocation('');
      setObservations('');
      setSerial('');
      setShowSerialField(false);
      setSerialOption('No');
      setSelectedBrand('');
      setEvidencePhotos([]);
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
      <h3 className="section-title">Entrada de Inventario</h3>
        <button
          type="button"
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          onClick={() => setBulkOpen(true)}
        >
          Entrada masiva (Excel)
        </button>
      </div>

      {lastReceipt && (
        <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Movimiento registrado: {lastReceipt.type}</p>
            <p className="text-xs text-gray-600">{lastReceipt.materialName} — Cant: {lastReceipt.quantity} — {lastReceipt.date}</p>
          </div>
          <button type="button" onClick={() => printMovement(lastReceipt)} className="bg-gray-800 text-white px-3 py-2 rounded flex items-center gap-2">
            <Printer size={16} /> Imprimir comprobante
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Crear nuevo material inline removido: la creación se hará al registrar entrada si se requiere */}

        {/* Vincular a orden de compra (opcional) */}
        <div className="border rounded-lg p-4 bg-blue-50/40">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vincular a orden de compra (opcional)
          </label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
            value={selectedOcId}
            onChange={(e) => handleSelectOcForReview(e.target.value)}
            disabled={ocLoading}
          >
            <option value="">{ocLoading ? 'Cargando órdenes de compra...' : '— Ninguna —'}</option>
            {ocOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.provider || 'Sin proveedor'} ({o.status})</option>
            ))}
          </select>

          {selectedOcId && ocReviewRows.length > 0 && (
            <div className="mt-3 border rounded overflow-x-auto bg-white">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-700">
                    <th className="px-3 py-2 text-left">Ítem de la OC</th>
                    <th className="px-3 py-2 text-left">Material</th>
                    <th className="px-3 py-2 text-left">Pendiente</th>
                    <th className="px-3 py-2 text-left">Cantidad a ingresar hoy</th>
                  </tr>
                </thead>
                <tbody>
                  {ocReviewRows.map((r, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{r.description} <span className="text-gray-500">({r.unit})</span></td>
                      <td className="px-3 py-2">
                        {r.materialId ? (
                          (() => {
                            const m = materials.find((mm) => String(mm.id) === String(r.materialId));
                            return m ? (m.code ? `${m.code} - ${m.name}` : m.name) : r.materialId;
                          })()
                        ) : (
                          <select
                            className="border rounded px-2 py-1 text-sm"
                            value={r.materialId}
                            onChange={(e) => updateOcReviewRow(idx, { materialId: e.target.value })}
                          >
                            <option value="">Sin coincidencia — seleccionar material</option>
                            {materials.map((m) => (
                              <option key={m.id} value={m.id}>{m.code ? `${m.code} - ${m.name}` : m.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2">{r.ordered - r.alreadyReceived} de {r.ordered}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          className="w-24 border rounded px-2 py-1"
                          value={r.enteredQty}
                          onChange={(e) => updateOcReviewRow(idx, { enteredQty: Number(e.target.value) || 0 })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3">
                <button
                  type="button"
                  onClick={addOcRowsToBatch}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  <Plus className="h-4 w-4" /> Agregar ítems de la OC al lote
                </button>
                {!currentUser?.purchaseCanEditOrders && (
                  <span className="ml-3 text-xs text-gray-500">
                    La entrada quedará vinculada a la OC, pero necesitas permiso para marcarla como recibida automáticamente.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Entrada *</label>
            <div className="relative">
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="Compras">Compras</option>
                <option value="Traslado">Traslado</option>
                <option value="Cliente">Cliente</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bodega *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="Todas">Todas las categorías</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <div ref={wrapperRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Material *</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                placeholder="Escriba para buscar material..."
                value={materialQuery}
                onChange={(e) => {
                  setMaterialQuery(e.target.value);
                  setMaterialId('');
                  setIsMaterialDropdownOpen(true);
                }}
                onFocus={() => setIsMaterialDropdownOpen(true)}
              />
              {isMaterialDropdownOpen && (
                <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                  {filteredMaterialOptions.length > 0 ? (
                    filteredMaterialOptions.map((o) => (
                      <li
                        key={o.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => {
                          setMaterialId(String(o.id));
                          setMaterialQuery(o.label);
                          setIsMaterialDropdownOpen(false);
                        }}
                      >
                        {o.label}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500 text-sm">No se encontraron resultados</li>
                  )}
                </ul>
              )}
            </div>
            {/* Selector de marcas mostrado automáticamente por tipo */}
          </div>
        </div>

        {/* Resumen del material removido */}

        {/* Row 2: Cantidad, Proveedor, Ubicación y bloque Serie/Marca */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor/Origen</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              type="text"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="Nombre del proveedor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej: A-01"
            />
          </div>

          <div>
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serie</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-gray-400 focus:border-transparent text-sm"
                    value={serialOption}
                    onChange={(e) => handleSerialOptionChange(e.target.value)}
                    disabled={requiresSerialMaterial}
                  >
                    <option value="No">No</option>
                    <option value="Sí">Sí</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  {brandsForMaterial.length > 0 ? (
                    <select
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-gray-400 focus:border-transparent text-sm"
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <option value="">Seleccione...</option>
                      {brandsForMaterial.map((b) => (
                        <option key={b.id} value={b.name}>{b.name}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-gray-400 focus:border-transparent text-sm opacity-60"
                      disabled
                    >
                      <option>Sin marcas configuradas para este tipo</option>
                    </select>
                  )}
                </div>
              </div>
              
            </div>
          </div>
        </div>

        {/* Campo Serial condicional */}
        {showSerialField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Serie</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              type="text"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="Ingrese la serie"
              />
            </div>
        )}

        {/* Observaciones */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 h-28 resize-y focus:ring-2 focus:ring-gray-400 focus:border-transparent"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            placeholder="Detalles adicionales..."
          />
        </div>

        {/* Registro fotográfico */}
        <div>
          <PhotoEvidenceModule
            value={evidencePhotos}
            onChange={setEvidencePhotos}
          />
        </div>

        {/* Lista de ítems a ingresar */}
        <div className="border rounded-lg overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="px-4 py-2 text-left">Material</th>
                <th className="px-4 py-2 text-left">Cantidad</th>
                <th className="px-4 py-2 text-left">Proveedor</th>
                <th className="px-4 py-2 text-left">Ubicación</th>
                <th className="px-4 py-2 text-left">Serie</th>
                <th className="px-4 py-2 text-left">Marca</th>
                <th className="px-4 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const mat = materials.find((m) => m.id === parseInt(it.materialId));
                return (
                  <tr key={idx} className="border-t">
                    <td className="px-4 py-2">
                      {mat ? (mat.code ? `${mat.code} - ${mat.name}` : `${mat.id} - ${mat.name}`) : it.materialId}
                      {it.ocCode && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">OC {it.ocCode}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">{it.quantity}</td>
                    <td className="px-4 py-2">{it.supplier || '—'}</td>
                    <td className="px-4 py-2">{(it.location || '').trim() || '—'}</td>
                    <td className="px-4 py-2">{(it.serial || '').trim() || '—'}</td>
                    <td className="px-4 py-2">{(it.brandName || '').trim() || '—'}</td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => removeItem(idx)}
                        aria-label="Eliminar"
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">No hay ítems agregados. Use "Agregar ítem".</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            disabled={!canSubmitSingle}
            onClick={handleAddItem}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Agregar ítem
          </button>
          <button
            type="submit"
            disabled={!canSubmitBulk}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Guardar
          </button>
          <button
            type="button"
            onClick={() => {
            // Reinicia el formulario y limpia la lista de ítems
            setItems([]);
              setQuantity(1);
              setSupplier('');
          setObservations('');
          setSerial('');
          setShowSerialField(false);
          setSelectedBrand('');
          setLocation('');
          setEvidencePhotos([]);
          setStatusMsg('Ingreso cancelado. Formulario reiniciado.');
        }}
        className="inline-flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
        >
          <X size={18} /> Cancelar
        </button>
        </div>

      {statusMsg && (
        <p className={`text-sm pt-2 ${/error\(es\): [1-9]|problema/.test(statusMsg) ? 'font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2' : 'text-gray-600'}`}>{statusMsg}</p>
      )}
      </form>

      <Modal
        isOpen={bulkOpen}
        title="Entrada masiva (Excel)"
        onClose={() => {
          if (bulkSaving) return;
          setBulkOpen(false);
        }}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              className="bg-gray-800 text-white px-3 py-2 rounded"
              onClick={downloadBulkTemplate}
            >
              Descargar plantilla
            </button>
            <div className="text-xs text-gray-600">
              Columnas: Material, Marca, Modelo (opcional), Serial (si aplica), Cantidad
            </div>
          </div>

          <div>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={bulkSaving}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                setBulkFileName(f?.name || '');
                setBulkParsed(null);
                setBulkErrors([]);
                if (f) await parseBulkFile(f);
              }}
            />
            {bulkFileName && <div className="text-xs text-gray-500 mt-1">{bulkFileName}</div>}
          </div>

          {bulkErrors.length > 0 && (
            <div className="border border-red-300 bg-red-50 text-red-800 rounded p-3 text-sm space-y-1">
              {bulkErrors.slice(0, 10).map((e, i) => (
                <div key={i}>{e}</div>
              ))}
              {bulkErrors.length > 10 && <div>… y {bulkErrors.length - 10} más</div>}
            </div>
          )}

          {bulkParsed && bulkErrors.length === 0 && (
            <div className="border border-gray-200 rounded p-3 text-sm space-y-2">
              <div>
                Materiales: <span className="font-semibold">{(bulkParsed.materials || []).length}</span>
              </div>
              <div>
                Total filas válidas: <span className="font-semibold">{bulkParsed.rowsCount}</span>
              </div>
              <div>
                Grupos a registrar: <span className="font-semibold">{(bulkParsed.materials || []).reduce((acc, m) => acc + ((m.serialGroups || []).length + (m.qtyGroups || []).length), 0)}</span>
              </div>
              <div className="overflow-x-auto border rounded">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700">
                  <th className="px-3 py-2 text-left">Material</th>
                  <th className="px-3 py-2 text-left">Marca</th>
                      <th className="px-3 py-2 text-left">Cantidad</th>
                      <th className="px-3 py-2 text-left">Seriales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rows = [];
                      for (const m of (bulkParsed.materials || [])) {
                        for (const g of (m.serialGroups || [])) {
                          rows.push({ materialCode: m.materialCode || '-', brand: g.brand || '-', qty: (Array.isArray(g.serials) ? g.serials.length : 0), serials: (Array.isArray(g.serials) ? g.serials.length : 0) });
                        }
                        for (const g of (m.qtyGroups || [])) {
                          rows.push({ materialCode: m.materialCode || '-', brand: g.brand || '-', qty: parseInt(g.quantity) || 0, serials: 0 });
                        }
                      }
                      const preview = rows.slice(0, 20);
                      return (
                        <>
                          {preview.map((r, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-3 py-2">{r.materialCode}</td>
                              <td className="px-3 py-2">{r.brand}</td>
                              <td className="px-3 py-2">{r.qty}</td>
                              <td className="px-3 py-2">{r.serials}</td>
                            </tr>
                          ))}
                          {rows.length > 20 && (
                            <tr className="border-t"><td className="px-3 py-2" colSpan={4}>… y {rows.length - 20} grupos más</td></tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded border"
              disabled={bulkSaving}
              onClick={() => setBulkOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              disabled={bulkSaving || !bulkParsed || bulkErrors.length > 0 || (bulkParsed?.materials?.length || 0) === 0}
              onClick={submitBulk}
            >
              Registrar entrada masiva
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
